<template>
  <div v-if="init" class="amap-measure-tool">
    <Teleport to="#amap-control">
      <div :class="{ 'measure-control': true, 'measure-active': measuring }">
        <el-tooltip content="测距工具" effect="dark" placement="left">
          <img src="@p-v/packages/Map/AMapChart/assets/rule.png" alt="测距工具" class="measure-icon" @click="startMeasure" />
        </el-tooltip>
      </div>
    </Teleport>
    <Teleport to="#amap-info-window">
      <div class="measure-info" v-if="measuring">
        <div class="distance-info" v-if="totalDistance > 0">
          <div class="messure-result">
            <div>{{ isClosed ? "周长" : "总距离" }}: {{ formatDistance(totalDistance) }}</div>
            <div v-if="isClosed && area > 0" class="area-info">面积: {{ formatArea(area) }}</div>
          </div>
          <div class="measure-btns">
            <el-button type="" @click="cancelMeasure" class="measure-btn finish">完成测距</el-button>
          </div>
        </div>

        <div v-else>请点击地图开始测距</div>

        <div v-if="points.length >= 3 && !isClosed" class="tip-info">提示: 点击第一个点可闭合路径并计算面积</div>
      </div>
    </Teleport>
  </div>
</template>

<script lang="ts" setup>
import { ref, inject, onMounted, onUnmounted, watch } from "vue"
import { ElButton, ElTooltip } from "element-plus"

defineOptions({
  name: "AMapMeasure",
})

const init = ref<boolean>(false)

const props = defineProps({
  visible: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits(["measure-complete", "measure-cancel"])

// 从父组件注入地图实例
const amap = inject<any>("amap")

// 测距状态
const measuring = ref<boolean>(false)
const points = ref<any[]>([])
const markers = ref<any[]>([])
const polyline = ref<any | null>(null)
const polygon = ref<any | null>(null)
const totalDistance = ref<number>(0)
const area = ref<number>(0)
const isClosed = ref<boolean>(false)
// 第一个点的标记，用于检测闭合
const firstPointMarker = ref<any | null>(null)
// 闭合路径的距离阈值（单位：米）
const closeThreshold = 20

// 监听地图实例变化
watch(
  () => amap.value,
  map => {
    if (map) {
      // 初始化测量工具
      console.log("初始化测量工具", map)
      init.value = true
    }
  },
  { immediate: true },
)

// 开始测距
const startMeasure = () => {
  if (!amap.value) return

  measuring.value = true
  points.value = []
  totalDistance.value = 0
  area.value = 0
  isClosed.value = false
  firstPointMarker.value = null

  // 清除之前的标记
  clearMarkers()

  // 添加点击事件监听器
  amap.value.on("click", handleMapClick)
}

const scale = inject("scale", ref(1))

// 处理地图点击
const handleMapClick = (e: any) => {
  if (isClosed.value) {
    calculateTotalDistance()
    // 如果路径已闭合，忽略点击事件
    return
  }

  const { pixel } = e

  const x = pixel.x / scale.value
  const y = pixel.y / scale.value

  const lnglat = amap.value.containerToLngLat(new window.AMap.Pixel(x, y))

  // console.log("点击位置经纬度:", lnglat, scale)

  const position = [lnglat.getLng(), lnglat.getLat()]

  // 检查是否点击回到第一个点（仅当至少有3个点时才考虑闭合）
  if (points.value.length >= 3 && firstPointMarker.value) {
    const firstPoint = new window.AMap.LngLat(points.value[0][0], points.value[0][1])
    const clickPoint = new window.AMap.LngLat(position[0], position[1])
    const distance = firstPoint.distance(clickPoint)

    // 如果点击位置与第一个点的距离小于阈值，则闭合路径
    if (distance < closeThreshold) {
      // 使用第一个点的坐标作为闭合点，确保精确闭合
      const closingPosition = points.value[0]

      // 闭合路径
      isClosed.value = true

      // 创建多边形并计算面积
      createPolygon()
      calculateArea()

      return // 不再添加新点
    }
  }

  // 添加点位到数组
  points.value.push(position)

  // 添加标记点
  addMarker(position, points.value.length)

  // 更新折线
  updatePolyline()

  // 计算总距离
  calculateTotalDistance()
}

// 添加标记点
const addMarker = (position: number[], index: number) => {
  if (!amap.value) return

  const marker = new window.AMap.Marker({
    position,
    draggable: false,
    cursor: "pointer",
  })

  if (index === 1) {
    marker.on("click", () => {
      if (points.value.length >= 3 && firstPointMarker.value) {
        // 闭合路径
        isClosed.value = true

        // 计算总距离
        calculateTotalDistance()

        // 创建多边形并计算面积
        createPolygon()
        calculateArea()
      }
    })
  }

  amap.value.add(marker)
  markers.value.push(marker)

  // 保存第一个点的标记，用于后续检测闭合
  if (index === 1) {
    firstPointMarker.value = marker
  }
}

// 更新折线
const updatePolyline = () => {
  if (!amap.value || points.value.length < 2) return

  // 清除旧的折线
  if (polyline.value) {
    amap.value.remove(polyline.value)
  }

  // 创建新折线
  polyline.value = new window.AMap.Polyline({
    path: points.value,
    isOutline: true,
    outlineColor: "#ffeeff",
    borderWeight: 1,
    strokeColor: "#3366FF",
    strokeOpacity: 0.9,
    strokeWeight: 3,
    strokeStyle: "solid",
    lineJoin: "round",
    lineCap: "round",
    zIndex: 50,
  })

  // 添加到地图
  amap.value.add(polyline.value)
}

// 创建多边形
const createPolygon = () => {
  if (!amap.value || points.value.length < 3) return

  // 清除旧的多边形
  if (polygon.value) {
    amap.value.remove(polygon.value)
  }

  // 创建新多边形
  polygon.value = new window.AMap.Polygon({
    path: points.value,
    strokeColor: "#3366FF",
    strokeWeight: 3,
    strokeOpacity: 0.9,
    fillColor: "#3366FF",
    fillOpacity: 0.1,
    zIndex: 40,
  })

  // 添加到地图
  amap.value.add(polygon.value)

  // 如果有折线，移除它
  if (polyline.value) {
    amap.value.remove(polyline.value)
    polyline.value = null
  }
}

// 计算多边形面积
const calculateArea = () => {
  if (!points.value.length || points.value.length < 3) {
    area.value = 0
    return
  }

  try {
    // 使用高德地图API计算面积
    const ringArea = window.AMap.GeometryUtil.ringArea(points.value)
    area.value = Math.abs(ringArea)
  } catch (error) {
    console.error("计算面积失败", error)
    area.value = 0
  }
}

// 计算总距离
const calculateTotalDistance = () => {
  if (points.value.length < 2) {
    totalDistance.value = 0
    return
  }

  let distance = 0
  for (let i = 0; i < points.value.length - 1; i++) {
    const start = new window.AMap.LngLat(points.value[i][0], points.value[i][1])
    const end = new window.AMap.LngLat(points.value[i + 1][0], points.value[i + 1][1])
    distance += start.distance(end)
  }
  console.log("计算的总距离:", distance, isClosed.value)
  if (isClosed.value && points.value.length > 2) {
    // 如果路径闭合，计算最后一段距离
    const firstPoint = new window.AMap.LngLat(points.value[0][0], points.value[0][1])
    const lastPoint = new window.AMap.LngLat(points.value[points.value.length - 1][0], points.value[points.value.length - 1][1])
    distance += lastPoint.distance(firstPoint)
  }

  totalDistance.value = distance
}

// 格式化距离显示
const formatDistance = (distance: number) => {
  if (distance < 1000) {
    return `${distance.toFixed(2)} 米`
  } else {
    return `${(distance / 1000).toFixed(2)} 公里`
  }
}

// 格式化面积显示
const formatArea = (areaValue: number) => {
  if (areaValue < 1000000) {
    return `${areaValue.toFixed(2)} 平方米`
  } else {
    return `${(areaValue / 1000000).toFixed(4)} 平方公里`
  }
}

// 完成测距
const finishMeasure = () => {
  amap.value.off("click", handleMapClick)
  measuring.value = false

  // 发送测量完成事件
  emit("measure-complete", {
    points: points.value,
    totalDistance: totalDistance.value,
    area: isClosed.value ? area.value : 0,
    isClosed: isClosed.value,
  })
}

// 取消测距
const cancelMeasure = () => {
  amap.value.off("click", handleMapClick)
  measuring.value = false
  clearMarkers()

  // 发送取消事件
  emit("measure-cancel")
}

// 清除所有标记
const clearMarkers = () => {
  if (!amap.value) return

  // 清除标记点
  if (markers.value.length > 0) {
    markers.value.forEach(marker => {
      amap.value.remove(marker)
    })
    markers.value = []
  }

  // 清除折线
  if (polyline.value) {
    amap.value.remove(polyline.value)
    polyline.value = null
  }

  // 清除多边形
  if (polygon.value) {
    amap.value.remove(polygon.value)
    polygon.value = null
  }

  // 重置第一个点的标记引用
  firstPointMarker.value = null
}

// 组件卸载清理
onUnmounted(() => {
  if (measuring.value && amap.value) {
    amap.value.off("click", handleMapClick)
  }
  clearMarkers()
})

// 暴露方法
defineExpose({
  startMeasure,
  finishMeasure,
  cancelMeasure,
  clearMarkers,
})
</script>

<style scoped>
.amap-measure-tool {
  /* position: absolute;
  top: 5px;
  right: 5px;
  z-index: 100; */
}

.measure-control {
  background-color: white;
  padding: 5px;
  filter: grayscale(1);
  img {
    width: 18px;
    height: 18px;
    cursor: pointer;
  }
}

.measure-active {
  filter: none;
}

.measure-btn {
  padding: 3px 6px;
  margin: 0 2px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 12px;
  transition: all 0.2s;
  line-height: 20px;
  height: 20px;
}

.measure-btn.start {
  background-color: #3366ff;
  color: white;
  width: 100%;
}

.measure-btn.finish {
  background-color: #52c41a;
  color: white;
}

.measure-btn.cancel {
  background-color: #f5222d;
  color: white;
}

.measure-btns {
  display: flex;
  justify-content: space-between;
}

.measure-info {
  font-size: 14px;
  background-color: rgba(110, 110, 110, 0.6);
  display: flex;
  flex-direction: column;
  padding: 5px;
  font-size: 12px;
  border-radius: 4px;
}

.points-info,
.distance-info {
  margin: 0;
}

.messure-result {
  margin: 0 5px;
}

.distance-info {
  font-weight: bold;
  color: #ffffff;
  display: flex;
  justify-content: space-between;

  align-items: center;
}

.area-info {
  font-weight: bold;
  color: #ffffff;
}

.tip-info {
  font-size: 12px;
  color: #fff;
  font-style: italic;
}
</style>
